import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation, withTranslation } from 'react-i18next';

import { withBrand } from '../../../../providers/BrandProvider';
import Button from '../../../shared/Button/Button';
import VatMessage from '../../VatMessage/VatMessage';
import PaymentFormBlock from '../PaymentFormBlock/PaymentFormBlock';
import { getTicketCount } from '../../../../store/pricingDucks/chosenProducts';
import { getCarbonPriceMapper } from '../../../../store/pricingDucks/siteProducts';
import { getTierFormattedDate } from '../ProductType/ProductItem/ProductItem';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Link from '../../../shared/Link/Link';
import VatRulesBox from '../VatRulesBox/VatRulesBox';

import './TotalPriceBox.scss';

export function TotalPriceBox(props) {
  const {
    totalCalculation: {
      packageDiscount,
      tieredSaving,
      subtotalInclVat,
      subtotal,
      totalVat,
      totalInclVat,
      total,
    } = {},
    tier: { endDate: tierEndDate } = {},
    taxLabelStyle = 'VAT',
    priceInclVat = false,
    vatRate = 0,
    vatInputs,
    vatFormIsDisabled,
    deliveryType,
    quantity,
    carbonOffset = '0',
    carbonPriceMapper,
    onQuantityChange,
    onAddToBasket,
    onVatRulesChange,
    brand: { secondaryColor } = {},
    ticketCount,
    errors,
    dateFormat,
  } = props;
  const { t } = useTranslation();
  const isCarbonOffsetApply = carbonOffset !== '0' && carbonOffset !== '';

  return (
    <div className="c-total-price-box">
      <h4
        className="c-total-price-box__heading"
        style={{ color: secondaryColor }}
      >
        {t('event.purchase.heading.your.price')}
      </h4>
      {!vatFormIsDisabled && (
        <VatRulesBox
          vatInputs={vatInputs}
          vatFormErrors={errors}
          onVatRulesChange={onVatRulesChange}
        />
      )}
      <PaymentFormBlock className="c-total-price-box__basket">
        <div className="row">
          <div className="col-xs-6">
            <div className="text-left">{t('event.purchase.quantity')}</div>
          </div>
          <div className="col-xs-6">
            <div className="text-right">
              <input
                type="number"
                disabled={deliveryType === 'DISTANCE'}
                className="c-total-price-box__basket-quantity"
                onChange={onQuantityChange}
                value={quantity}
                min={1}
                max={999}
              />
            </div>
          </div>
        </div>
        {isCarbonOffsetApply && (
          <div className="row" data-test-total-price="carbon-offset-row">
            <div className="col-xs-6">
              <div className="text-left">
                {t('site.purchase.select-package.carbon.name')}
              </div>
            </div>
            <div className="col-xs-6">
              <div
                className="text-right"
                data-test-total-price="carbon-offset-value"
              >
                {carbonPriceMapper[carbonOffset]}
              </div>
            </div>
          </div>
        )}
        <div className="row">
          <div className="col-xs-6">
            <div className="text-left">
              {t('event.purchase.subtotal')}
              {tieredSaving && tierEndDate && (
                <div className="c-total-price-box__basket-saving-message">
                  {t('event.purchase.saving-if-booked-by', {
                    0: getTierFormattedDate(tierEndDate, dateFormat),
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="col-xs-6">
            <div className="text-right">
              <div className="c-total-price-box__basket-subtotal">
                {!priceInclVat ? subtotal : subtotalInclVat}
              </div>
              {tieredSaving && (
                <div className="c-total-price-box__basket-saving-message">
                  <div className="c-total-price-box__basket-tiered-saving">
                    {tieredSaving}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {packageDiscount && (
          <div className="row">
            <div className="col-xs-6">
              <div className="text-left">
                {t('event.purchase.package-discount')}
              </div>
            </div>
            <div className="col-xs-6">
              <div className="text-right">
                <div className="c-total-price-box__basket-package-discount">
                  {'- '}
                  {packageDiscount}
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="row">
          <div className="col-xs-6">
            <div className="text-left">
              {t('event.purchase.total')}
              {priceInclVat && (
                <VatMessage
                  isPriceInclVat={priceInclVat}
                  vatRate={vatRate}
                  taxLabelStyle={taxLabelStyle}
                  isCarbonOffsetApply={isCarbonOffsetApply}
                  className="c-vat-message--total-price-vat"
                />
              )}
            </div>
          </div>
          <div className="col-xs-6">
            <div className="text-right">
              <div className="c-total-price-box__basket-total">
                {priceInclVat ? totalInclVat : total}
              </div>
              {priceInclVat ? (
                <VatMessage vat={totalVat} />
              ) : (
                <VatMessage
                  isPriceInclVat={priceInclVat}
                  vat={totalVat}
                  vatRate={vatRate}
                  taxLabelStyle={taxLabelStyle}
                  isCarbonOffsetApply={isCarbonOffsetApply}
                />
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12">
            {ticketCount && deliveryType === 'DISTANCE' ? (
              <Link
                to="/purchase/view-basket"
                type="button"
                look="accent"
                size="large"
                event
                className="c-total-price-box__basket-submit"
              >
                {t('event.continue')}
              </Link>
            ) : (
              <Button
                look="accent"
                size="large"
                onClick={onAddToBasket}
                className="c-total-price-box__basket-submit"
              >
                {t('event.purchase.add-to-basket')}
              </Button>
            )}
          </div>
        </div>
      </PaymentFormBlock>
    </div>
  );
}

TotalPriceBox.propTypes = {
  totalCalculation: PropTypes.object,
  deliveryType: PropTypes.string,
  quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  carbonOffset: PropTypes.string,
  tier: PropTypes.shape({
    endDate: PropTypes.string,
  }),
  vatRate: PropTypes.number,
  vatFormIsDisabled: PropTypes.bool,
  taxLabelStyle: PropTypes.string,
  priceInclVat: PropTypes.bool,
  vatInputs: PropTypes.array,
  errors: PropTypes.object,
  onQuantityChange: PropTypes.func,
  onAddToBasket: PropTypes.func,
  onVatRulesChange: PropTypes.func,
  brand: PropTypes.object,
  ticketCount: PropTypes.number,
  carbonPriceMapper: PropTypes.object,
};

function mapStateToProps(state) {
  return {
    ticketCount: getTicketCount(state),
    carbonPriceMapper: getCarbonPriceMapper(state),
  };
}

export default withTranslation()(
  connect(mapStateToProps)(withBrand(withRouter(TotalPriceBox))),
);
